import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";

export const WashtenawCountyTextParser = () => {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseWashtenawData = (text: string) => {
    const records = [];
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    let currentRecord: any = null;
    let currentSection = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if this is a new record (starts with liber/page info)
      if (line.startsWith('L:') && line.includes('•')) {
        // Save previous record if exists
        if (currentRecord) {
          records.push(currentRecord);
        }
        
        // Parse the header line: "L: 5575 P: 404  •  6742396  •  Land Contract"
        const parts = line.split('•').map(p => p.trim());
        const liberPage = parts[0]; // "L: 5575 P: 404"
        const docNumber = parts[1]; // "6742396"
        const docType = parts[2]?.replace('Visited', '').trim(); // "Land Contract"
        
        currentRecord = {
          document_number: docNumber,
          document_type: docType,
          raw_data: { liber_page: liberPage },
          grantor_names: [],
          grantee_names: []
        };
        currentSection = '';
      }
      // Check for Recording Date
      else if (line === 'Recording Date') {
        currentSection = 'recording_date';
      }
      // Check for Grantor section
      else if (line.startsWith('Grantor')) {
        currentSection = 'grantor';
      }
      // Check for Grantee section
      else if (line.startsWith('Grantee')) {
        currentSection = 'grantee';
      }
      // Parse data based on current section
      else if (currentRecord) {
        if (currentSection === 'recording_date') {
          // Parse date like "12/30/2024 03:39 PM"
          const dateMatch = line.match(/(\d{2}\/\d{2}\/\d{4})/);
          if (dateMatch) {
            const [month, day, year] = dateMatch[1].split('/');
            currentRecord.filing_date = `${year}-${month}-${day}`;
            currentSection = '';
          }
        }
        else if (currentSection === 'grantor') {
          currentRecord.grantor_names.push(line);
        }
        else if (currentSection === 'grantee') {
          currentRecord.grantee_names.push(line);
        }
      }
    }
    
    // Don't forget the last record
    if (currentRecord) {
      records.push(currentRecord);
    }
    
    // Format the records for database insertion
    return records.map(record => ({
      county: 'Washtenaw',
      search_date: new Date().toISOString().split('T')[0],
      document_number: record.document_number,
      filing_date: record.filing_date,
      document_type: record.document_type,
      grantor_name: record.grantor_names.join(', '),
      grantee_name: record.grantee_names.join(', '),
      is_potential_owner_finance: true, // All land contracts are owner-financed
      confidence_score: 100,
      detection_reasons: ['Land Contract document type'],
      status: 'pending_review',
      is_active: true,
      address_needs_review: true,
      pdf_downloaded: false,
      pdf_parsed: false,
      ocr_attempted: false,
      raw_data: record.raw_data
    }));
  };

  const handleProcess = async () => {
    if (!rawText.trim()) {
      toast({
        title: "Error",
        description: "Please paste the data first",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const parsedRecords = parseWashtenawData(rawText);
      
      if (parsedRecords.length === 0) {
        toast({
          title: "No Records Found",
          description: "Could not parse any records from the provided text",
          variant: "destructive",
        });
        setRawText("");
        return;
      }

      // Check for existing records
      const docNumbers = parsedRecords.map(r => r.document_number);
      const { data: existingRecords } = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'Washtenaw')
        .in('document_number', docNumbers);

      const existingDocNumbers = new Set(existingRecords?.map(r => r.document_number) || []);
      const newRecords = parsedRecords.filter(r => !existingDocNumbers.has(r.document_number));

      if (newRecords.length === 0) {
        toast({
          title: "All Records Already Exist",
          description: `Found ${parsedRecords.length} records, but all already exist in database`,
        });
        setRawText("");
        return;
      }

      // Insert new records
      const { error } = await supabase
        .from('county_research')
        .insert(newRecords);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Imported ${newRecords.length} new land contract records${existingDocNumbers.size > 0 ? ` (${existingDocNumbers.size} duplicates skipped)` : ''}`,
      });
      
      setRawText("");
    } catch (error) {
      console.error('Error processing records:', error);
      toast({
        title: "Error",
        description: "Failed to process records",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Washtenaw County Land Contract Parser</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Paste land contract records from Washtenaw County, MI. The system will automatically parse
            grantor/grantee names, document numbers, and filing dates.
          </p>
        </div>

        <Textarea
          placeholder="Paste Washtenaw County land contract data here..."
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          className="min-h-[400px] font-mono text-sm"
        />

        <div className="flex gap-2">
          <Button 
            onClick={handleProcess}
            disabled={isProcessing || !rawText.trim()}
          >
            {isProcessing ? "Processing..." : "Process Records"}
          </Button>
          <Button 
            variant="outline"
            onClick={() => setRawText("")}
            disabled={isProcessing}
          >
            Clear
          </Button>
        </div>
      </div>
    </Card>
  );
};
